import React, { ReactNode, useState, useMemo } from 'react';
import { Avatar, Popover, List, Button } from 'antd';
import { useAuthUser } from '../../states/user.atom';
import { UserOutlined } from '@ant-design/icons';

export interface AvatarPopoverProps {
  /**
   * The avatar image URL or fallback text.
   */
  avatarContent?: ReactNode;
  /**
   * Additional list items to render after the defaults.
   */
  extraListItems?: ReactNode[];
  /**
   * Optionally override the default list entirely.
   */
  overrideList?: ReactNode[];
  /**
   * Popover placement.
   */
  placement?: 'top' | 'left' | 'right' | 'bottom';
}

const avatarStyle = { cursor: 'pointer' } as const;

export const AvatarPopover: React.FC<AvatarPopoverProps> = ({
  avatarContent,
  extraListItems = [],
  overrideList,
  placement = 'bottom',
}) => {
  const [open, setOpen] = useState(false);
  const { activeUser } = useAuthUser();

  const defaultList: ReactNode[] = useMemo(() => {
    const listItems = [];
    if (activeUser) {
      listItems.push(
        <Button type="text" icon={<UserOutlined />}>
          {activeUser.name}
        </Button>
      );
    }
    return listItems;
  }, [activeUser]);

  const listContent = useMemo(() => {
    return overrideList ?? [...defaultList, ...extraListItems];
  }, [defaultList, extraListItems, overrideList]);

  const popoverContent = useMemo(
    () => (
      <List
        dataSource={listContent}
        renderItem={(item, index) => (
          <List.Item key={index} style={{ padding: '4px 0' }}>
            {item}
          </List.Item>
        )}
      />
    ),
    [listContent]
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={placement}
    >
      <Avatar style={avatarStyle} size="large" src={activeUser?.picture}>
        {avatarContent}
      </Avatar>
    </Popover>
  );
};
